import React, { useState } from 'react';
import { Search, Loader2, CheckCircle2, Clock, FileSearch, Inbox, UserCheck, ShieldCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { trackAidRequest } from '../lib/api';

const STAGES = [
  { key: 'Submitted', label: 'Submitted', description: 'We received your request and assigned a tracking number.', icon: Inbox },
  { key: 'In Review', label: 'In Review', description: 'Our coordinators are verifying your eligibility and matter details.', icon: FileSearch },
  { key: 'Advocate Assigned', label: 'Advocate Assigned', description: 'A verified advocate has been allocated and will reach out to you.', icon: UserCheck },
  { key: 'Closed', label: 'Closed', description: 'Your matter has been concluded. Thank you for trusting us.', icon: ShieldCheck },
];

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const stageIcon = (stage, isCurrent, isReached) => {
  if (isCurrent) return stage.icon;
  if (isReached) return CheckCircle2;
  return stage.icon;
};

const stageCircleClasses = (isCurrent, isReached) => {
  if (isCurrent) return 'bg-[#E89530] border-[#E89530] text-[#0F1B2D]';
  if (isReached) return 'bg-[#0F1B2D] border-[#0F1B2D] text-[#E89530]';
  return 'bg-white border-[#D6CAB0] text-[#A0967D]';
};

const Track = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!trackingNumber.trim() || !phone.trim()) {
      toast.error('Enter both your tracking number and phone.');
      return;
    }
    setLoading(true);
    try {
      const data = await trackAidRequest({
        tracking_number: trackingNumber.trim().toUpperCase(),
        phone: phone.trim(),
      });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not find this request.';
      setError(typeof detail === 'string' ? detail : 'Lookup failed.');
    } finally {
      setLoading(false);
    }
  };

  const activeIndex = result ? STAGES.findIndex((s) => s.key === result.status) : -1;

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">REQUEST STATUS</p>
          <h1 className="font-serif-besley text-5xl md:text-6xl mb-5">Track your free aid request</h1>
          <p className="text-[#C9BFA8] text-lg max-w-2xl">
            Enter the tracking number you received and the phone number on the original request. Status updates are posted as our coordinators progress your matter.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <form onSubmit={handleSubmit} className="bg-white border border-[#E8DCC0] rounded-2xl p-8 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-[#0F1B2D]">Tracking number</Label>
                <Input
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="LFL-2026-0001"
                  className="mt-2 h-11 border-[#E8DCC0] focus-visible:ring-[#2D4373] uppercase tracking-wider"
                />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Phone on the request</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="10-digit phone or last 4 digits"
                  className="mt-2 h-11 border-[#E8DCC0] focus-visible:ring-[#2D4373]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-orange inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium disabled:opacity-70"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? 'Looking up\u2026' : 'Check status'}
            </button>
            {error && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                {error}
              </p>
            )}
          </form>

          {result && (
            <div className="mt-10 bg-[#EDE3CC] border border-[#D6CAB0] rounded-2xl p-8">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                <div>
                  <p className="text-xs tracking-[0.25em] text-[#A36B1F] mb-1">TRACKING NUMBER</p>
                  <h2 className="font-serif-besley text-3xl text-[#0F1B2D]">{result.tracking_number}</h2>
                  <p className="text-sm text-[#3a3a3a] mt-2">
                    Matter: <span className="font-medium text-[#0F1B2D]">{result.matter}</span>
                  </p>
                </div>
                <div className="text-sm text-[#3a3a3a]">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#A36B1F]" />
                    Last updated: {formatDate(result.updatedAt)}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Inbox className="w-4 h-4 text-[#A36B1F]" />
                    Submitted: {formatDate(result.createdAt)}
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-5 top-5 bottom-5 w-px bg-[#D6CAB0]" aria-hidden="true" />
                <ol className="space-y-6">
                  {STAGES.map((s, i) => {
                    const reached = activeIndex >= i;
                    const current = activeIndex === i;
                    const Icon = stageIcon(s, current, reached);
                    return (
                      <li key={s.key} className="relative pl-14">
                        <div
                          className={`absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center border-2 ${stageCircleClasses(current, reached)}`}
                        >
                          <Icon className="w-5 h-5" strokeWidth={1.8} />
                        </div>
                        <div>
                          <h3
                            className={`font-serif-besley text-xl ${
                              reached ? 'text-[#0F1B2D]' : 'text-[#7a7a7a]'
                            }`}
                          >
                            {s.label}
                            {current && (
                              <span className="ml-3 align-middle text-[11px] uppercase tracking-wider bg-[#E89530] text-[#0F1B2D] px-2 py-0.5 rounded">
                                Current
                              </span>
                            )}
                          </h3>
                          <p className={`text-sm leading-relaxed mt-1 ${reached ? 'text-[#3a3a3a]' : 'text-[#9a9a9a]'}`}>
                            {s.description}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          )}

          {!result && !error && (
            <p className="text-sm text-[#5a5a5a] text-center mt-8">
              Tip: tracking numbers look like <span className="font-mono text-[#0F1B2D]">LFL-2026-0001</span>. They are sent on the success screen right after you submit a free-aid request.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Track;
